import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import WhatsAppTemplateVariableMappingEditor from './WhatsAppTemplateVariableMappingEditor';
import { buildVariableMappings } from './whatsappVariableMappings';
import { parseConsecutiveTemplateSlots } from './whatsappTemplateSlots';
import { fetchDeepLinkCampaigns } from '../../services/MasterAdmin/deepLinkService';
import { fetchWhatsAppHubSettings } from '../../services/MasterAdmin/whatsappHubService';
import { getApiErrorMessage } from '../../utils/apiErrorMessage';
import type { WhatsAppContextField, WhatsAppTemplateVariableMapping } from '../../models/MasterAdmin/whatsappHub';
import type { DeepLinkCampaign } from '../../models/MasterAdmin/deepLink';

function upsertCampaign(list: DeepLinkCampaign[], campaign: DeepLinkCampaign): DeepLinkCampaign[] {
  const without = list.filter((c) => c.id !== campaign.id);
  return [campaign, ...without];
}

export default function WhatsAppTemplateMappingsSection({
  bodyPreview,
  mappings,
  onMappingsChange,
  onSave,
  saveLabel = 'Save variable mappings',
  showSaveButton = false,
  saving = false,
  title = 'Variable mappings',
  description = 'Configure once per template. Notifications and campaigns inherit these mappings automatically.',
  headerActions = null,
}: {
  bodyPreview: string;
  mappings: WhatsAppTemplateVariableMapping[];
  onMappingsChange: (next: WhatsAppTemplateVariableMapping[]) => void;
  onSave?: () => void | Promise<void>;
  saveLabel?: string;
  showSaveButton?: boolean;
  saving?: boolean;
  title?: string;
  description?: string;
  headerActions?: React.ReactNode;
}) {
  const [catalogFields, setCatalogFields] = useState<WhatsAppContextField[]>([]);
  const [deepLinkCampaigns, setDeepLinkCampaigns] = useState<DeepLinkCampaign[]>([]);

  const slotInfo = useMemo(() => parseConsecutiveTemplateSlots(bodyPreview), [bodyPreview]);

  const reloadDeepLinkCampaigns = useCallback(async () => {
    try {
      const campaigns = await fetchDeepLinkCampaigns();
      setDeepLinkCampaigns(campaigns || []);
    } catch (err: unknown) {
      console.warn('[WhatsAppTemplateMappings] Failed to refresh deep links:', getApiErrorMessage(err, 'unknown'));
    }
  }, []);

  useEffect(() => {
    fetchWhatsAppHubSettings()
      .then((res) => {
        const catalog = res.data.referenceGuide?.variableCatalog;
        if (catalog?.allContextFields?.length) {
          setCatalogFields(catalog.allContextFields);
        }
      })
      .catch(() => setCatalogFields([]));
    void reloadDeepLinkCampaigns();
  }, [reloadDeepLinkCampaigns]);

  useEffect(() => {
    const onFocus = () => {
      void reloadDeepLinkCampaigns();
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void reloadDeepLinkCampaigns();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [reloadDeepLinkCampaigns]);

  useEffect(() => {
    if (slotInfo.count <= 0) {
      if (mappings.length > 0) onMappingsChange([]);
      return;
    }
    if (mappings.length !== slotInfo.count) {
      onMappingsChange(buildVariableMappings(slotInfo.count, mappings, catalogFields));
      return;
    }
    if (!catalogFields.length) return;
    const synced = buildVariableMappings(slotInfo.count, mappings, catalogFields);
    const labelsStale = synced.some((m, idx) => m.label !== mappings[idx]?.label);
    if (labelsStale) onMappingsChange(synced);
  }, [slotInfo.count, catalogFields, bodyPreview]);

  return (
    <div className="mt-4 space-y-4 rounded-xl border border-[#25D366]/20 bg-[#25D366]/5 p-4 dark:border-[#25D366]/30 dark:bg-[#25D366]/10">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h4 className="text-sm font-semibold text-gray-900 dark:text-white">{title}</h4>
          <p className="mt-1 text-xs text-gray-500">{description}</p>
        </div>
        {headerActions}
      </div>

      {!slotInfo.valid && slotInfo.count > 0 && (
        <p className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100">
          Placeholders must be consecutive: missing {slotInfo.gaps.map((g) => `{{${g}}}`).join(', ')}
        </p>
      )}

      <WhatsAppTemplateVariableMappingEditor
        mappings={mappings}
        fields={catalogFields}
        campaigns={deepLinkCampaigns}
        onCampaignCreated={(c) => setDeepLinkCampaigns((prev) => upsertCampaign(prev, c))}
        onCampaignUpdated={(c) => setDeepLinkCampaigns((prev) => upsertCampaign(prev, c))}
        onRefreshCampaigns={() => void reloadDeepLinkCampaigns()}
        onChange={onMappingsChange}
      />

      {showSaveButton && onSave && (
        <button
          type="button"
          disabled={saving || !slotInfo.valid}
          onClick={() => void onSave()}
          className="rounded-lg bg-[#128C7E] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0e6b60] disabled:opacity-60"
        >
          {saving ? 'Saving…' : saveLabel}
        </button>
      )}
    </div>
  );
}

export function validateTemplateMappings(
  mappings: WhatsAppTemplateVariableMapping[],
  bodyPreview: string
): string | null {
  const slotInfo = parseConsecutiveTemplateSlots(bodyPreview);
  if (slotInfo.count > 0 && !slotInfo.valid) {
    return `Fix body placeholders first — use {{1}} through {{${slotInfo.count}}} with no gaps.`;
  }
  if (slotInfo.count > 0 && mappings.length !== slotInfo.count) {
    return 'Configure a mapping for each template variable.';
  }
  if (mappings.some((m) => m.sourceType === 'deep_link' && !m.deepLinkCampaignId)) {
    return 'Select a deep link campaign for all deep link variables.';
  }
  if (mappings.some((m) => m.sourceType === 'context' && !m.contextKey)) {
    return 'Select a data field for each context variable.';
  }
  return null;
}
